/**
 * URL Validation Utilities
 * Prevents open redirect vulnerabilities by validating redirect URLs against an allowlist
 */

import { REDIRECT_CONFIG } from '../config';

/**
 * Check if a URL is valid and belongs to an allowed domain
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is valid and allowed, false otherwise
 */
export const isValidRedirectUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      console.warn('Invalid protocol in URL:', urlObj.protocol);
      return false;
    }

    // Check if hostname matches allowed domains
    const isAllowed = REDIRECT_CONFIG.ALLOWED_DOMAINS.some(domain => {
      // Exact match or subdomain match
      return urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`);
    });

    if (!isAllowed) {
      console.warn('Domain not in allowlist:', urlObj.hostname);
    }

    return isAllowed;
  } catch (error) {
    // Invalid URL format
    console.warn('Invalid URL format:', url, error);
    return false;
  }
};

/**
 * Sanitize a redirect URL, returning the URL if valid or a fallback if not
 * @param {string} url - The URL to sanitize
 * @param {string} fallback - The fallback URL to use if validation fails
 * @returns {string} - The sanitized URL or fallback
 */
export const sanitizeRedirectUrl = (url, fallback = REDIRECT_CONFIG.FALLBACK_URL) => {
  if (!url || !isValidRedirectUrl(url)) {
    console.warn('Invalid redirect URL blocked:', url);
    console.info('Using fallback URL:', fallback);
    return fallback;
  }
  return url;
};

/**
 * Validate and extract query parameters from a URL
 * @param {string} url - The URL to parse
 * @returns {Object|null} - Object containing query parameters, or null if invalid
 */
export const parseQueryParams = (url) => {
  if (!isValidRedirectUrl(url)) {
    return null;
  }

  try {
    const urlObj = new URL(url);
    const params = {};

    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });

    return params;
  } catch (error) {
    console.error('Error parsing URL query params:', error);
    return null;
  }
};

/**
 * Check if a URL is safe for external navigation
 * @param {string} url - The URL to check
 * @returns {boolean} - True if safe to navigate, false otherwise
 */
export const isSafeExternalUrl = (url) => {
  // Check basic validation first
  if (!isValidRedirectUrl(url)) {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Additional security checks
    // 1. No javascript: protocol
    if (urlObj.protocol === 'javascript:') {
      console.error('JavaScript protocol detected - potential XSS attempt');
      return false;
    }

    // 2. No data: protocol
    if (urlObj.protocol === 'data:') {
      console.error('Data protocol detected - potential security risk');
      return false;
    }

    // 3. Check for suspicious patterns in path
    const suspiciousPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+=/i, // Event handlers like onclick=
    ];

    const fullUrl = urlObj.href;
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(fullUrl)) {
        console.error('Suspicious pattern detected in URL:', pattern);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error validating external URL:', error);
    return false;
  }
};

/**
 * Safely navigate to a URL, with validation
 * @param {string} url - The URL to navigate to
 * @param {string} fallback - Fallback URL if validation fails
 * @returns {boolean} - True if navigation attempted, false if blocked
 */
export const safeNavigate = (url, fallback = REDIRECT_CONFIG.FALLBACK_URL) => {
  const sanitizedUrl = sanitizeRedirectUrl(url, fallback);

  if (!isSafeExternalUrl(sanitizedUrl)) {
    console.error('Navigation blocked - URL failed safety check');
    return false;
  }

  try {
    window.location.href = sanitizedUrl;
    return true;
  } catch (error) {
    console.error('Navigation failed:', error);
    return false;
  }
};
